import React, { useEffect, useState } from "react";
import axios from "axios";
import { getAuthHeader } from "../../../utils/authHeader";
import type { InitialSchedule } from "./types";

// Soft, informational note in the post composer: if the week a post is being
// scheduled into is already at (or past) the user's Autopilot weekly ceiling
// for that channel, say so. NEVER blocks anything — the user is allowed to
// be wrong about their own account (product decision, autopilot design Q4).
// Renders nothing while loading, on error, or when there is nothing to say.

interface UsageRow {
    week: string;
    platform: string;
    count: number;
    ceiling: number;
}

const KEY_TO_PLATFORM: Record<string, string> = {
    facebook: "Facebook",
    instagram: "Instagram",
    twitter: "Twitter",
    linkedin: "LinkedIn",
    pinterest: "Pinterest",
    google: "Google",
    youtube: "YouTube",
};

const PLATFORM_LABEL: Record<string, string> = {
    Facebook: "Facebook",
    Instagram: "Instagram",
    Twitter: "X / Twitter",
    LinkedIn: "LinkedIn",
    Pinterest: "Pinterest",
    Google: "Google Business",
    YouTube: "YouTube",
};

// ISO week key ("2026-W29") from a local Date — same Monday-start bucketing
// the backend uses (user timezone ≈ browser timezone for a soft note).
function isoWeekKey(d: Date): string {
    const date = new Date(
        Date.UTC(d.getFullYear(), d.getMonth(), d.getDate())
    );
    const day = date.getUTCDay() || 7;
    date.setUTCDate(date.getUTCDate() + 4 - day);
    const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
    const week = Math.ceil(
        ((date.getTime() - yearStart.getTime()) / 86400000 + 1) / 7
    );
    return `${date.getUTCFullYear()}-W${String(week).padStart(2, "0")}`;
}

interface AutopilotUsageNoteProps {
    platformSchedules: Record<string, InitialSchedule>;
}

const AutopilotUsageNote: React.FC<AutopilotUsageNoteProps> = ({
    platformSchedules,
}) => {
    const [usage, setUsage] = useState<UsageRow[] | null>(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const backendUrl = process.env.REACT_APP_BACKEND_URL;
                const headers = await getAuthHeader();
                const response = await axios.get(
                    `${backendUrl}/autopilot/weekly-usage`,
                    { headers }
                );
                if (!cancelled) setUsage(response.data?.usage ?? []);
            } catch {
                // Soft feature: no usage data, no note.
                if (!cancelled) setUsage([]);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    if (!usage || usage.length === 0) return null;

    const warnings: string[] = [];
    for (const [key, schedule] of Object.entries(platformSchedules)) {
        const platform = KEY_TO_PLATFORM[key];
        const date = schedule?.date;
        if (!platform || !date) continue;
        const week = isoWeekKey(new Date(date));
        const row = usage.find(
            (u) => u.platform === platform && u.week === week
        );
        if (row && row.count >= row.ceiling) {
            warnings.push(
                `${PLATFORM_LABEL[platform]} already has ${row.count} post${
                    row.count === 1 ? "" : "s"
                } that week (guideline: ${row.ceiling}/week)`
            );
        }
    }

    if (warnings.length === 0) return null;

    return (
        <div className="bg-[#e9c468]/20 border border-[#e9c468] rounded-md p-3">
            <p className="text-sm text-[#193456]">
                <strong>Heads up:</strong> {warnings.join("; ")}. Posting past
                the weekly guideline can reduce reach on that channel. Your
                post will still be scheduled exactly as you set it.
            </p>
        </div>
    );
};

export default AutopilotUsageNote;
